'use client';

import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from '@/components/ui/spinner';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useSession } from '@descope/nextjs-sdk/client';
import { LoginHeader } from '@/components/login/LoginHeader';
import { LoginFooter } from '@/components/login/LoginFooter';
import { LoginSideBanner } from '@/components/login/LoginSideBanner';
import { TrialContent } from '@/components/login/TrialContent';
import { AuthFormContent } from '@/components/login/AuthFormContent';
import { useDescopeButtonListeners } from '@/hooks/useDescopeButtonListeners';

declare global {
    interface Window {
        amplitude?: any;
    }
}

export default function Login() {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const modeParam = searchParams.get('mode');
    const isSignupVerificationFlow = modeParam === 'signup' && searchParams.has('t');
    const initialMode = modeParam === 'signup'
        ? 'signup'
        : modeParam === 'trial'
            ? 'trial'
            : 'login';

    const [isLoading, setIsLoading] = useState(true);
    const [mode, setMode] = useState<'trial' | 'login' | 'signup'>(
        initialMode === 'signup' ? 'signup' : initialMode === 'trial' ? 'trial' : 'login'
    );
    const [redirecting, setRedirecting] = useState(false);
    const [sessionGateExpired, setSessionGateExpired] = useState(false);
    const { isAuthenticated, isSessionLoading } = useSession();
    const defaultRedirectAfterSuccess = mode === 'signup' ? '/onboarding' : '/';
    const rawRedirectPath = searchParams.get('redirect_to') || defaultRedirectAfterSuccess;
    const redirectAfterSuccess = rawRedirectPath.startsWith('/') && !rawRedirectPath.startsWith('//')
        ? rawRedirectPath
        : defaultRedirectAfterSuccess;

    const addAllButtonListeners = useDescopeButtonListeners(mode === 'trial' ? 'login' : mode);

    useEffect(() => {
        if (redirecting) return;
        if (isAuthenticated) {
            router.replace(redirectAfterSuccess);
        }
    }, [isAuthenticated, redirecting, router, redirectAfterSuccess]);

    useEffect(() => {
        if (!isSessionLoading) {
            setSessionGateExpired(false);
            return;
        }

        const timeout = setTimeout(() => {
            setSessionGateExpired(true);
        }, 3000);

        return () => clearTimeout(timeout);
    }, [isSessionLoading]);

    // Force signup mode when in email verification flow (e.g. magic link confirmation)
    useEffect(() => {
        if (isSignupVerificationFlow && mode !== 'signup') {
            setMode('signup');
        }
    }, [isSignupVerificationFlow, mode]);

    const handleModeChange = (mode: 'trial' | 'login' | 'signup') => {
        setMode(mode);
        const newSearchParams = new URLSearchParams(searchParams.toString());
        newSearchParams.set('mode', mode);
        router.push(`${pathname}?${newSearchParams.toString()}`);
    };

    let content;
    if ((isSessionLoading && !sessionGateExpired) || isAuthenticated || redirecting) {
        content = (
            <div className="flex w-full min-h-screen justify-center items-center">
                <LoadingSpinner />
            </div>
        );
    } else {
        content = (
            <div className="flex w-full min-h-screen p-[24px]">
                <div className="login-wrapper w-full flex flex-col lg:pr-8">
                    <LoginHeader showQuotaPartnership={searchParams.get('utm_medium')?.toLowerCase() === 'thequota'} />
                    {mode === 'trial' && (
                        <TrialContent onModeChange={handleModeChange} />
                    )}
                    {(mode === 'signup' || mode === 'login') && (
                        <div className="flex flex-col h-full w-full justify-center md:items-center text-center gap-4 px-0 md:px-2 flex-grow md:w-[424px] self-center">
                            <AuthFormContent
                                mode={mode}
                                searchParams={searchParams}
                                redirectAfterSuccess={redirectAfterSuccess}
                                redirectUrl={
                                    typeof window !== 'undefined'
                                        ? (() => {
                                              const p = new URLSearchParams(searchParams.toString());
                                              p.set('mode', mode);
                                              return `${window.location.origin}${pathname}?${p.toString()}`;
                                          })()
                                        : undefined
                                }
                                isLoading={isLoading}
                                onLoadingChange={setIsLoading}
                                onRedirecting={() => setRedirecting(true)}
                                onSuccess={addAllButtonListeners}
                                onModeChange={handleModeChange}
                            />
                        </div>
                    )}
                    <LoginFooter />
                </div>
                <LoginSideBanner />
            </div>
        );
    }

    return content;
}
